import * as React from "react"
import * as ProgressTabsPrimitives from "@radix-ui/react-tabs"
import { clx } from "../utils/clx"
import { ChevronRightMiniIcon } from "~/design-system"

/**
 * This component is based on the [Radix UI Tabs](https://radix-ui.com/primitives/docs/components/tabs) primitves.
 *
 * @excludeExternal
 */
const ProgressTabsRoot = (props: ProgressTabsPrimitives.TabsProps) => {
  return <ProgressTabsPrimitives.Root {...props} />
}
ProgressTabsRoot.displayName = "ProgressTabs"

interface ProgressTabsTriggerProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ProgressTabsPrimitives.Trigger>,
    "asChild"
  > {
  step: number
}

const ProgressTabsTrigger = React.forwardRef<
  React.ElementRef<typeof ProgressTabsPrimitives.Trigger>,
  ProgressTabsTriggerProps
>(
  (
    {
      className,
      children,
      step,
      ...props
    },
    ref
  ) => (
    <>
      <ProgressTabsPrimitives.Trigger
        ref={ref}
        className={clx(
          "group txt-base-regular-plus text-ui-fg-base border shadow-borders-with-shadow rounded-full inline-flex min-w-0 max-w-[220px] items-center justify-center gap-x-2 p-1 pr-3 truncate text-left outline-none transition-fg",
          "disabled:bg-ui-bg-disabled disabled:text-ui-fg-muted disabled:border-transparent",
          "focus-visible:bg-ui-bg-base focus:z-[1]",
          "data-[state=active]:border-blue-base data-[state=active]:shadow-none",
          className
        )}
        {...props}
      >
        <div 
          role="presentation"
          className={clx(
            "bg-grey-10 text-ui-fg-subtle font-semibold w-[26px] h-[26px] flex items-center justify-center shrink-0 rounded-full",
            "group-data-[state=active]:bg-blue-base group-data-[state=active]:text-grey-0 group-disabled:text-ui-fg-muted group-disabled:border group-disabled:border-ui-border-strong",
          )}
        >
          {step}
        </div>
        {children}
      </ProgressTabsPrimitives.Trigger>
      <ChevronRightMiniIcon className="text-grey-30 last:hidden" />
    </>
  )
)
ProgressTabsTrigger.displayName = "ProgressTabs.Trigger"

const ProgressTabsList = React.forwardRef<
  React.ElementRef<typeof ProgressTabsPrimitives.List>,
  React.ComponentPropsWithoutRef<typeof ProgressTabsPrimitives.List>
>(({ className, ...props }, ref) => (
  <ProgressTabsPrimitives.List
    ref={ref}
    className={clx("flex items-center", className)}
    {...props}
  />
))
ProgressTabsList.displayName = "ProgressTabs.List"

const ProgressTabsContent = React.forwardRef<
  React.ElementRef<typeof ProgressTabsPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof ProgressTabsPrimitives.Content>
>(({ className, ...props }, ref) => {
  return (
    <ProgressTabsPrimitives.Content
      ref={ref}
      className={clx("outline-none", className)}
      {...props}
    />
  )
})
ProgressTabsContent.displayName = "ProgressTabs.Content"

const ProgressTabs = Object.assign(ProgressTabsRoot, {
  Trigger: ProgressTabsTrigger,
  List: ProgressTabsList,
  Content: ProgressTabsContent,
})

export { ProgressTabs }
